import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import {
  accentNidra as accent, useNidra, Seg,
} from "./yoganidra.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Yoga Nidra in ALL mode — scrollable, no canvas.
   (The Practice-mode YogaNidraBody keeps the interactive body-scan instead.) */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#ffffff", fontWeight: 700 }}>{s.t}</b>
    : s.hl ? <span key={i} style={{ color: "#c8b0e8" }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const YogaNidraMobile: React.FC = () => {
  const { meta: nidraMeta, technique: nidraTechnique, intro: nidraIntro, threshold: nidraThreshold } = useNidra();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#8a6ab0", textTransform: "uppercase", marginBottom: 8 }}>
        {nidraMeta.breadcrumbPre} · <span style={{ color: accent }}>{nidraMeta.breadcrumbAccent}</span>
      </div>

      {/* sanskrit + title */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, color: "rgba(192,144,224,0.55)", marginBottom: 6 }}>{nidraMeta.sanskrit}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 24, lineHeight: 1.2, margin: "0 0 8px", color: "#e8f4ff" }}>
        {nidraMeta.titlePre}<span style={{ color: accent }}>{nidraMeta.titleAccentWord}</span>{nidraMeta.titlePost}
      </h1>

      {/* intro */}
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14.5, lineHeight: 1.65, color: "#9bbdd6", margin: "0 0 20px" }}>
        {renderSegs(nidraIntro)}
      </p>

      {/* technique */}
      <div style={{ borderTop: "1px solid rgba(192,144,224,0.12)", paddingTop: 14, marginBottom: 18 }}>
        <div style={lbl}>{nidraMeta.techniqueLabel}</div>
        {nidraTechnique.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontFamily: mono, fontSize: 11, color: accent, opacity: 0.6, flexShrink: 0, lineHeight: 1.5, minWidth: "1.8em" }}>{s.n}</span>
            <div>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14.5, color: "#d8c0ff" }}>{s.title} </span>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, lineHeight: 1.55, color: "#9bbdd6" }}>{s.desc}</span>
              {s.sub && (
                <div style={{ marginTop: 4, fontFamily: mono, fontSize: 10.5, color: "rgba(192,144,224,0.55)", letterSpacing: 0.2, lineHeight: 1.6 }}>{s.sub}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* threshold callout */}
      <div style={{ background: "rgba(192,144,224,0.06)", border: "1px solid rgba(192,144,224,0.22)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{nidraMeta.thresholdLabel}</div>
        <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.6, color: "#d0b8f0", margin: 0 }}>{renderSegs(nidraThreshold)}</p>
      </div>
    </div>
  );
};

export default YogaNidraMobile;
